// api
import { getEmailSignaturesByUserId } from '@api/email-signature-api';
import { useUser } from '@context/user-context';
// hooks and context
import useOnce from '@hooks/use-once';
import {
  type EmailSignaturesActions,
  emailSignatureFieldsReducer,
  initialEmailSignatureContextValues,
} from '@reducers/email-signature-reducer';
import { type Dispatch, useReducer, useState } from 'react';
import type { User } from 'types';

import type { EmailSignatureV2 } from '../types';

/**
 * This hook is used to initialise the onboarding email signature for an anonymous user. It needs to know the onboarding email signature id and it will
 * check if the current user actually has an email signature with this id. If the user does not have any email signature with the given id, it will call
 * the callback function.
 * @param onboardingEmailSignatureId the onboarding email signature id
 */
function useOnboardingSignature(
  getOnboardingEmailSignatureId: (userId: string) => string | null,
): [EmailSignatureV2, Dispatch<EmailSignaturesActions>, boolean, boolean] {
  const [onboardingEmailSignature, onboardingEmailSignatureDispatch] = useReducer(
    emailSignatureFieldsReducer,
    initialEmailSignatureContextValues,
  );
  const [isEditingSavedEmailSignature, setIsEditingSavedEmailSignature] = useState(false);
  const [onboardingEmailSignatureHasLoaded, setOnboardingEmailSignatureHasLoaded] = useState(false);
  const { user, loading: userIsLoading } = useUser();

  useOnce(
    (preventSecondRun) => {
      if (userIsLoading) {
        return;
      }

      setOnboardingEmailSignatureHasLoaded(false);
      if (!user) {
        setOnboardingEmailSignatureHasLoaded(true);
        preventSecondRun();
        return;
      }

      async function fetchEmailSignature(onboardingEmailSignatureId: string | null, user: User) {
        const emailSignatures = await getEmailSignaturesByUserId(user.userId);
        const onboardingEmailSignature = emailSignatures.filter(
          (onboardingEmailSignature) => onboardingEmailSignature.id === onboardingEmailSignatureId,
        );

        if (onboardingEmailSignature.length === 0) {
          // start from scratch if no cached email signature founded
          onboardingEmailSignatureDispatch({
            type: 'RESET_EMAIL_SIGNATURE',
          });
        } else {
          onboardingEmailSignatureDispatch({
            type: 'IMPORT_EMAIL_SIGNATURE',
            value: onboardingEmailSignature[0],
          });
          setIsEditingSavedEmailSignature(true);
        }
        setOnboardingEmailSignatureHasLoaded(true);
      }

      const onboardingEmailSignatureId = getOnboardingEmailSignatureId(user.userId);
      fetchEmailSignature(onboardingEmailSignatureId, user);

      preventSecondRun();
    },
    [getOnboardingEmailSignatureId, user, userIsLoading],
  );

  return [
    onboardingEmailSignature,
    onboardingEmailSignatureDispatch,
    onboardingEmailSignatureHasLoaded,
    isEditingSavedEmailSignature,
  ];
}

export default useOnboardingSignature;
